import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import EditIcon from '@mui/icons-material/Edit';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';

import {
  useCreatePurchaseOrderNoteMutation,
  useUpdatePurchaseOrderNoteMutation,
  useGetPurchaseOrderNoteQuery,
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
} from '../../../store/api/purchaseApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import ProductFormContent from '../../products/pages/ProductFormContent';
import { SupplierFormContent } from './SupplierForm';
import { formatItemDisplayName } from '../../sales/utils/formatItemDisplayName';
import { exportPurchaseOrderPdf } from '../utils/exportPurchaseOrderPdf';

function PurchaseOrderNoteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const outletContext = useOutletContext();
  const setNavigationBlocked = outletContext?.setNavigationBlocked;

  const [createPurchaseOrderNote, { isLoading: isCreating }] = useCreatePurchaseOrderNoteMutation();
  const [updatePurchaseOrderNote, { isLoading: isUpdating }] = useUpdatePurchaseOrderNoteMutation();
  const { data: existingPo, isLoading: isLoadingPo } = useGetPurchaseOrderNoteQuery(id, {
    skip: !isEdit,
  });

  const { data: initialSuppliersData, isFetching: loadingInitialSuppliers } = useGetSuppliersQuery({ limit: 100 });
  const [triggerSupplierSearch, { data: supplierSearchData, isFetching: searchingSuppliers }] =
    useLazyGetSuppliersQuery();
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();

  const { data: company } = useGetCurrentCompanyQuery();
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const currency = company?.currency || 'USD';
  const showWholesalePrice = company?.showWholesalePrice !== false;

  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [productDialog, setProductDialog] = useState({ open: false, productId: null });
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, existingIndex: -1, product: null });
  const [productSearch, setProductSearch] = useState('');
  const productSearchInputRef = useRef(null);
  const [focusQtyIndex, setFocusQtyIndex] = useState(null);
  const qtyInputRefs = useRef({});

  const [formData, setFormData] = useState({
    poNumber: `PO-${Date.now().toString().slice(-6)}`,
    supplierId: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    status: 'draft',
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    isTaxable: true,
  });

  const [items, setItems] = useState([]);

  // Preload initial products for immediate autocomplete dropdown
  useEffect(() => {
    triggerProductSearch({ limit: 50 });
  }, [triggerProductSearch]);

  // F1 or F2 keyboard shortcut to focus product search input
  useEffect(() => {
    const handleHelp = (e) => {
      e.preventDefault();
      return false;
    };
    window.onhelp = handleHelp;

    const handleKeyDown = (e) => {
      if (e.key === 'F1' || e.key === 'F2') {
        e.preventDefault();
        e.stopPropagation();
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
          try {
            productSearchInputRef.current.select();
          } catch (err) {}
          return;
        }
        const el =
          document.getElementById('product-search-input') ||
          document.querySelector('input[placeholder*="barcode"]');
        if (el) {
          if (el.tagName === 'INPUT') {
            el.focus();
            try {
              el.select();
            } catch (err) {}
          } else {
            const inner = el.querySelector('input');
            if (inner) {
              inner.focus();
              try {
                inner.select();
              } catch (err) {}
            }
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.onhelp = null;
    };
  }, []);

  useEffect(() => {
    const list =
      supplierSearchData?.data ||
      supplierSearchData?.suppliers ||
      supplierSearchData ||
      initialSuppliersData?.data ||
      initialSuppliersData?.suppliers ||
      initialSuppliersData;
    if (Array.isArray(list)) {
      setSupplierSearchOptions(list);
    }
  }, [supplierSearchData, initialSuppliersData]);

  // Populate data when editing existing PO
  useEffect(() => {
    if (existingPo && isEdit) {
      setFormData({
        poNumber: existingPo.poNumber || '',
        supplierId: existingPo.supplierId || '',
        orderDate: existingPo.orderDate ? existingPo.orderDate.split('T')[0] : '',
        expectedDeliveryDate: existingPo.expectedDeliveryDate
          ? existingPo.expectedDeliveryDate.split('T')[0]
          : '',
        status: existingPo.status || 'draft',
        notes: existingPo.notes || '',
        discountTotal: Number(existingPo.discountTotal) || 0,
        roundOff: Number(existingPo.roundOff) || 0,
        isTaxable: existingPo.isTaxable !== false,
      });

      if (existingPo.supplier) {
        setSupplierSearchOptions([existingPo.supplier]);
      }

      if (existingPo.items) {
        setItems(
          existingPo.items.map((it) => ({
            productId: it.productId,
            name: it.product?.name || '',
            sku: it.product?.sku || '',
            brand: it.product?.brand || '',
            barcode: it.product?.barcode || '',
            unit: it.unit || it.product?.unit || 'pcs',
            quantity: Number(it.quantity) || 1,
            unitPrice: Number(it.unitPrice) || 0,
            sellingPrice: it.sellingPrice != null ? Number(it.sellingPrice) : '',
            wholesalePrice: it.wholesalePrice != null ? Number(it.wholesalePrice) : '',
            discount: Number(it.discount) || 0,
            vatPercent: Number(it.vatPercent) || 0,
            vatAmount: Number(it.vatAmount) || 0,
            total: Number(it.total) || 0,
          }))
        );
      }
    }
  }, [existingPo, isEdit]);

  // Focus quantity input when an item is added
  useEffect(() => {
    if (focusQtyIndex !== null && qtyInputRefs.current[focusQtyIndex]) {
      qtyInputRefs.current[focusQtyIndex].focus();
      try {
        qtyInputRefs.current[focusQtyIndex].select();
      } catch (err) {}
      setFocusQtyIndex(null);
    }
  }, [focusQtyIndex, items]);

  const handleAddProduct = useCallback(
    (product, forceNew = false) => {
      if (!product) return;
      const behavior =
        company?.barcodeAfterScanBehaviorPurchase || company?.barcodeAfterScanBehavior || 'quantity';
      const dupBehavior =
        company?.duplicateItemBehaviorPurchase ||
        (behavior === 'auto_increment' ? 'auto_increment' : behavior === 'add_new' ? 'add_new' : 'dialog');
      const existingIndex = items.findIndex((i) => String(i.productId) === String(product.id));

      if (existingIndex > -1 && !forceNew) {
        if (dupBehavior === 'auto_increment') {
          const updated = [...items];
          const curQty = Number(updated[existingIndex].quantity || 0) || 0;
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: String(Math.max(0.01, curQty + 1)),
          };
          setItems(updated);
          setTimeout(() => {
            const el = document.getElementById('product-search-input');
            if (el) {
              el.focus();
              try {
                el.select();
              } catch (err) {}
            }
          }, 100);
          return;
        }

        if (dupBehavior !== 'add_new') {
          setDuplicateDialog({ open: true, existingIndex, product });
          if (behavior === 'search') {
            setTimeout(() => {
              const el = document.getElementById('product-search-input');
              if (el) {
                el.focus();
                try {
                  el.select();
                } catch (err) {}
              }
            }, 120);
          } else if (behavior === 'cost' || behavior === 'price') {
            setTimeout(() => {
              const el = document.getElementById(`cost-input-${existingIndex}`);
              if (el) {
                el.focus();
                try {
                  el.select();
                } catch (err) {}
              }
            }, 120);
          } else if (behavior === 'quantity') {
            setTimeout(() => {
              const el = qtyInputRefs.current[existingIndex];
              if (el) {
                el.focus();
                try {
                  el.select();
                } catch (err) {}
              }
            }, 120);
          }
          return;
        }
      }

      const costPrice = Number(product.costPrice) || 0;
      const sellingPrice = Number(product.sellingPrice) || 0;
      const wholesalePrice = Number(product.wholesalePrice) || 0;
      const newItem = {
        productId: product.id,
        name: product.name,
        sku: product.sku,
        brand: product.brand,
        barcode: product.barcode || '',
        unit: product.baseUnit || product.unit || 'pcs',
        quantity: 1,
        unitPrice: costPrice,
        sellingPrice: sellingPrice || '',
        wholesalePrice: wholesalePrice || '',
        discount: 0,
        vatPercent: 0,
        vatAmount: 0,
        total: costPrice,
      };

      setItems((prev) => [newItem, ...prev]);

      if (behavior === 'quantity') {
        setTimeout(() => {
          const el = qtyInputRefs.current[0];
          if (el) {
            el.focus();
            try {
              el.select();
            } catch (err) {}
          }
        }, 120);
      } else if (behavior === 'cost' || behavior === 'price') {
        setTimeout(() => {
          const el = document.getElementById('cost-input-0');
          if (el) {
            el.focus();
            try {
              el.select();
            } catch (err) {}
          }
        }, 120);
      } else {
        setTimeout(() => {
          const el = document.getElementById('product-search-input');
          if (el) {
            el.focus();
            try {
              el.select();
            } catch (err) {}
          }
        }, 120);
      }
      setProductSearch('');
    },
    [company, items]
  );

  // Scanner modal reset
  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  // Handle scanned barcodes
  useEffect(() => {
    if (!pendingBarcode) return;
    const code = String(pendingBarcode).trim();
    if (!code) return;

    const doBarcodeScan = async () => {
      let list = productSearchData?.products || [];
      let match = list.find(
        (p) =>
          String(p?.barcode || '').trim() === code ||
          String(p?.sku || '').trim() === code ||
          (Array.isArray(p?.additionalBarcodes) &&
            p.additionalBarcodes.some((b) => String(b.barcode || '').trim() === code))
      );

      if (!match) {
        try {
          const res = await triggerProductSearch({ search: code, limit: 10 }).unwrap();
          const fetchedList = res?.products || [];
          match =
            fetchedList.find(
              (p) =>
                String(p?.barcode || '').trim() === code ||
                String(p?.sku || '').trim() === code ||
                (Array.isArray(p?.additionalBarcodes) &&
                  p.additionalBarcodes.some((b) => String(b.barcode || '').trim() === code))
            ) || fetchedList[0];
        } catch (e) {
          console.error('Barcode lookup error:', e);
        }
      }

      if (match) {
        handleAddProduct(match);
        setProductSearch('');
        setPendingBarcode('');
        setScanOpen(false);
      }
    };

    doBarcodeScan();
  }, [pendingBarcode, productSearchData, handleAddProduct, triggerProductSearch]);

  // Auto-exact match when typing barcode or SKU
  useEffect(() => {
    const val = String(productSearch).trim();
    if (!val || val.length < 2) return;
    if (searchingProducts) return;
    const list = productSearchData?.products || [];
    if (list.length === 0) return;

    const match = list.find(
      (p) =>
        String(p?.barcode || '').trim().toLowerCase() === val.toLowerCase() ||
        String(p?.sku || '').trim().toLowerCase() === val.toLowerCase() ||
        (Array.isArray(p?.additionalBarcodes) &&
          p.additionalBarcodes.some(
            (b) => String(b.barcode || '').trim().toLowerCase() === val.toLowerCase()
          ))
    );

    if (match) {
      handleAddProduct(match);
      setProductSearch('');
    }
  }, [productSearchData, productSearch, searchingProducts, handleAddProduct]);

  const handleUpdateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
  };

  // Calculations
  const vatRate = formData.isTaxable ? Number(company?.vatRate || 0) : 0;
  const vatIncluded = company?.purchaseVatIncluded !== false;

  const subtotal = items.reduce(
    (sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitPrice) || 0),
    0
  );
  const discountTotal = Math.max(0, Math.min(parseFloat(formData.discountTotal) || 0, subtotal));
  const taxableSubtotal = Math.max(0, subtotal - discountTotal);

  let vatTotal = 0;
  let calculatedGrandTotal = 0;

  if (formData.isTaxable && vatRate > 0) {
    if (vatIncluded) {
      const net = taxableSubtotal / (1 + vatRate / 100);
      vatTotal = taxableSubtotal - net;
      calculatedGrandTotal = taxableSubtotal;
    } else {
      vatTotal = taxableSubtotal * (vatRate / 100);
      calculatedGrandTotal = taxableSubtotal + vatTotal;
    }
  } else {
    vatTotal = 0;
    calculatedGrandTotal = taxableSubtotal;
  }

  const roundOff = parseFloat(formData.roundOff) || 0;
  const grandTotal = calculatedGrandTotal + roundOff;

  const formatAmount = (v) => {
    const num = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2,
        maximumFractionDigits: typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2,
      }).format(num);
    } catch (e) {
      return `${currency} ${num.toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplierId) {
      alert('Please select a supplier');
      return;
    }
    if (!formData.orderDate) {
      alert('Please select an order date');
      return;
    }
    if (items.length === 0) {
      alert('Please add at least one product item');
      return;
    }

    const payload = {
      ...formData,
      discountTotal,
      roundOff,
      items: items.map((it) => ({
        productId: it.productId,
        quantity: parseFloat(it.quantity) || 1,
        unit: it.unit || undefined,
        unitPrice: parseFloat(it.unitPrice) || 0,
        sellingPrice: it.sellingPrice !== '' && it.sellingPrice != null ? parseFloat(it.sellingPrice) : undefined,
        wholesalePrice:
          it.wholesalePrice !== '' && it.wholesalePrice != null ? parseFloat(it.wholesalePrice) : undefined,
        discount: parseFloat(it.discount) || 0,
      })),
    };

    try {
      if (isEdit) {
        await updatePurchaseOrderNote({ id, ...payload }).unwrap();
        alert('Purchase order updated successfully');
      } else {
        await createPurchaseOrderNote(payload).unwrap();
        alert('Purchase order created successfully');
      }
      navigate('/purchases/order-notes');
    } catch (err) {
      alert(err?.data?.message || 'Failed to save purchase order');
    }
  };

  if (isLoadingPo) return <Typography>Loading purchase order...</Typography>;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/purchases/order-notes')}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {isEdit ? `Edit Purchase Order: ${formData.poNumber}` : 'New Purchase Order'}
          </Typography>
        </Box>
        {isEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ShoppingCartCheckoutIcon />}
              onClick={() => navigate(`/purchases/orders/new?fromPoId=${id}`)}
            >
              Convert to Purchase
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={() => exportPurchaseOrderPdf(existingPo, company)}
            >
              Print PDF
            </Button>
          </Box>
        )}
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Purchase Order Note:</strong> Creating or editing a Purchase Order does{' '}
        <strong>not affect stock inventory levels</strong> or create financial vouchers. You can fully
        edit items, quantities, and prices, and convert this order directly into a Purchase later.
      </Alert>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Top Form Fields Card */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    label="PO Number"
                    size="small"
                    value={formData.poNumber}
                    onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={supplierSearchOptions}
                      loading={searchingSuppliers || loadingInitialSuppliers}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        return `${option.name || ''}${option.phone ? ` (${option.phone})` : ''}`;
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      value={
                        supplierSearchOptions.find((s) => s.id === formData.supplierId) ||
                        (existingPo?.supplier && existingPo.supplier.id === formData.supplierId
                          ? existingPo.supplier
                          : null)
                      }
                      onChange={(_, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          supplierId: newValue?.id || '',
                        }));
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          if (newInputValue && newInputValue.length >= 1) {
                            triggerSupplierSearch({ search: newInputValue, limit: 50 });
                          } else {
                            triggerSupplierSearch({ limit: 100 });
                          }
                        }
                      }}
                      onOpen={() => {
                        if (supplierSearchOptions.length === 0) {
                          triggerSupplierSearch({ limit: 100 });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Supplier"
                          placeholder="Search supplier by name or phone..."
                          required
                        />
                      )}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => setSupplierDialogOpen(true)}
                      title="Add New Supplier"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <PersonAddAltIcon />
                    </IconButton>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    label="Order Date"
                    value={formData.orderDate}
                    onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    label="Expected Delivery"
                    value={formData.expectedDeliveryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expectedDeliveryDate: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="sent">Sent to Supplier</MenuItem>
                    <MenuItem value="confirmed">Confirmed by Supplier</MenuItem>
                    <MenuItem value="partially_received">Partially Received</MenuItem>
                    <MenuItem value="completed">Completed / Received</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes / Delivery Terms"
                    placeholder="e.g. Deliver to Main Warehouse, payment 30 days..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isTaxable}
                        onChange={(e) =>
                          setFormData({ ...formData, isTaxable: e.target.checked })
                        }
                        color="primary"
                      />
                    }
                    label={`Apply VAT (${company?.vatRate || 0}%)`}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Items Table & Unified Header Search */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ mb: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  bgcolor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Items ({items.length})
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'flex-end',
                    maxWidth: 600,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={() => setProductDialog({ open: true, productId: null })}
                    sx={{ flexShrink: 0 }}
                  >
                    New Product
                  </Button>
                  <Autocomplete
                    fullWidth
                    size="medium"
                    autoHighlight
                    freeSolo
                    options={searchingProducts ? [] : productSearchData?.products || []}
                    loading={searchingProducts}
                    filterOptions={(x) => x}
                    value={null}
                    inputValue={productSearch}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      if (typeof option === 'string') return option;
                      return formatItemDisplayName(
                        { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                        itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                      );
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box
                          sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.name}
                            </Typography>
                            {option.costPrice != null && (
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, color: 'primary.main' }}
                              >
                                Cost: {currency}{' '}
                                {Number(option.costPrice).toFixed(company?.currencyDecimals ?? 2)}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {option.barcode ? `Barcode: ${option.barcode}` : ''}
                            {option.sku ? ` • SKU: ${option.sku}` : ''}
                            {option.brand ? ` • Brand: ${option.brand}` : ''}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    onInputChange={(_, value) => {
                      setProductSearch(value);
                      if (value && value.trim().length >= 1) {
                        triggerProductSearch({ search: value.trim(), limit: 50 });
                      } else if (!value) {
                        triggerProductSearch({ limit: 50 });
                      }
                    }}
                    onChange={(_, value) => {
                      if (value && typeof value === 'object') {
                        handleAddProduct(value);
                        setProductSearch('');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={productSearchInputRef}
                        inputProps={{
                          ...params.inputProps,
                          id: 'product-search-input',
                        }}
                        id="product-search-input"
                        autoFocus
                        label="Scan or search item (name / barcode / SKU / brand) - [F1]"
                        placeholder="Type or paste name / barcode / SKU / brand"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const typed = String(productSearch).trim();
                            if (!typed) return;

                            let list = productSearchData?.products || [];
                            if (list.length === 0 || searchingProducts) {
                              try {
                                const res = await triggerProductSearch({
                                  search: typed,
                                  limit: 20,
                                }).unwrap();
                                list = res?.products || [];
                              } catch (err) {
                                console.error(err);
                              }
                            }

                            if (list.length > 0) {
                              const match =
                                list.find((p) => {
                                  const bMatch =
                                    String(p?.barcode || '').trim().toLowerCase() ===
                                    typed.toLowerCase();
                                  const skuMatch =
                                    String(p?.sku || '').trim().toLowerCase() ===
                                    typed.toLowerCase();
                                  const nameMatch =
                                    p?.name && p.name.toLowerCase() === typed.toLowerCase();
                                  const addlMatch =
                                    Array.isArray(p?.additionalBarcodes) &&
                                    p.additionalBarcodes.some(
                                      (b) =>
                                        String(b.barcode || '').trim().toLowerCase() ===
                                        typed.toLowerCase()
                                    );
                                  return bMatch || skuMatch || nameMatch || addlMatch;
                                }) ||
                                list.find((p) => {
                                  const nameIncludes =
                                    p?.name && p.name.toLowerCase().includes(typed.toLowerCase());
                                  const brandIncludes =
                                    p?.brand && p.brand.toLowerCase().includes(typed.toLowerCase());
                                  return nameIncludes || brandIncludes;
                                }) ||
                                list[0];

                              if (match) {
                                handleAddProduct(match);
                                setProductSearch('');
                              }
                            }
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 52,
                          },
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<QrCodeScannerIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => setScanOpen(true)}
                  >
                    Scan
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      if (productSearch && productSearch.trim().length >= 1) {
                        triggerProductSearch({ search: productSearch.trim(), limit: 20 });
                      }
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 140 }} align="center">
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 90 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">
                        Unit Cost ({currency})
                      </TableCell>
                      {showWholesalePrice && (
                        <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">
                          Wholesale Price
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">
                        Selling Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 110 }} align="right">
                        Discount ({currency})
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">
                        Line Total ({currency})
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length > 0 ? (
                      items.map((item, idx) => {
                        const qty = parseFloat(item.quantity) || 0;
                        const cost = parseFloat(item.unitPrice) || 0;
                        const disc = parseFloat(item.discount) || 0;
                        const lineTotal = Math.max(0, qty * cost - disc);

                        return (
                          <TableRow key={item.productId || idx} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatItemDisplayName(
                                  { name: item.name || 'Product', sku: item.sku, brand: item.brand },
                                  itemNameFormat
                                )}
                              </Typography>
                              {item.sku && (
                                <Typography variant="caption" color="text.secondary">
                                  SKU: {item.sku}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleUpdateItem(idx, 'quantity', Math.max(1, qty - 1))
                                  }
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  size="small"
                                  type="number"
                                  id={`qty-input-${idx}`}
                                  inputRef={(el) => (qtyInputRefs.current[idx] = el)}
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleUpdateItem(
                                      idx,
                                      'quantity',
                                      Math.max(0, parseFloat(e.target.value) || 0)
                                    )
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: 'any',
                                    style: { textAlign: 'center', width: 60, padding: '4px' },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateItem(idx, 'quantity', qty + 1)}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.unit || ''}
                                onChange={(e) => handleUpdateItem(idx, 'unit', e.target.value)}
                                inputProps={{ style: { padding: '4px 8px' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                id={`cost-input-${idx}`}
                                size="small"
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    idx,
                                    'unitPrice',
                                    Math.max(0, parseFloat(e.target.value) || 0)
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step: 'any',
                                  style: { textAlign: 'right', width: 90, padding: '4px 8px' },
                                }}
                              />
                            </TableCell>
                            {showWholesalePrice && (
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.wholesalePrice ?? ''}
                                  placeholder="–"
                                  onChange={(e) =>
                                    handleUpdateItem(idx, 'wholesalePrice', e.target.value)
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: 'any',
                                    style: { textAlign: 'right', width: 90, padding: '4px 8px' },
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.sellingPrice ?? ''}
                                placeholder="–"
                                onChange={(e) =>
                                  handleUpdateItem(idx, 'sellingPrice', e.target.value)
                                }
                                inputProps={{
                                  min: 0,
                                  step: 'any',
                                  style: { textAlign: 'right', width: 90, padding: '4px 8px' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.discount || ''}
                                placeholder="0"
                                onChange={(e) =>
                                  handleUpdateItem(
                                    idx,
                                    'discount',
                                    Math.max(0, parseFloat(e.target.value) || 0)
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step: 'any',
                                  style: { textAlign: 'right', width: 75, padding: '4px 8px' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatAmount(lineTotal)}
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  setProductDialog({ open: true, productId: item.productId })
                                }
                                sx={{ mr: 0.5 }}
                                title="Edit Product Details"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(idx)}
                                title="Remove Item"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={showWholesalePrice ? 10 : 9}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No items added yet. Search or scan products above to add them to this purchase order.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Bottom Summary and Action Buttons */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Order Summary & Adjustments
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={`Overall Order Discount (${currency})`}
                    value={formData.discountTotal || ''}
                    placeholder="0"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discountTotal: Math.max(0, parseFloat(e.target.value) || 0),
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={`Round Off (${currency})`}
                    value={formData.roundOff || ''}
                    placeholder="0"
                    onChange={(e) =>
                      setFormData({ ...formData, roundOff: parseFloat(e.target.value) || 0 })
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              sx={{
                p: 3,
                bgcolor: 'grey.50',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal ({items.length} items):</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatAmount(subtotal)}
                </Typography>
              </Box>

              {discountTotal > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="warning.main">
                    Discount:
                  </Typography>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                    - {formatAmount(discountTotal)}
                  </Typography>
                </Box>
              )}

              {formData.isTaxable && vatRate > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">VAT ({vatRate}%):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(vatTotal)}
                  </Typography>
                </Box>
              )}

              {roundOff !== 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Round Off:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(roundOff)}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pt: 1.5,
                  mt: 1.5,
                  borderTop: '2px solid rgba(0,0,0,0.12)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Grand Total:
                </Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                  {formatAmount(grandTotal)}
                </Typography>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/purchases/order-notes')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isCreating || isUpdating}
                  sx={{ minWidth: 160 }}
                >
                  {isCreating || isUpdating
                    ? 'Saving...'
                    : isEdit
                    ? 'Update Purchase Order'
                    : 'Save Purchase Order'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      {/* Barcode Scanner Dialog */}
      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Scan Barcode with Camera</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added to the order when matched.
            </Alert>
          )}
          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;
                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Item Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Item already added</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {duplicateDialog.product
              ? `“${duplicateDialog.product.name}${
                  duplicateDialog.product.sku ? ` (${duplicateDialog.product.sku})` : ''
                }” is already in the list.`
              : 'This item is already in the list.'}
          </Typography>
          <Typography color="text.secondary">
            Do you want to increase the quantity by 1, or add it as a new line?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
          >
            No
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              const product = duplicateDialog.product;
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
              if (product) {
                handleAddProduct(product, true);
              }
            }}
          >
            Add as new
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const idx = duplicateDialog.existingIndex;
              setItems((prev) => {
                if (idx < 0 || idx >= prev.length) return prev;
                const next = [...prev];
                const currentQty = Number(next[idx]?.quantity || 0) || 0;
                next[idx] = {
                  ...next[idx],
                  quantity: String(Math.max(0.01, currentQty + 1)),
                };
                return next;
              });
              setFocusQtyIndex(idx);
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
            }}
          >
            Yes, increase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Supplier</DialogTitle>
        <DialogContent>
          <SupplierFormContent
            isDialog
            onSuccess={(newSupplier) => {
              setSupplierDialogOpen(false);
              if (newSupplier) {
                setSupplierSearchOptions((prev) => [newSupplier, ...prev]);
                setFormData((prev) => ({ ...prev, supplierId: newSupplier.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add / Edit Product Dialog */}
      <Dialog
        open={productDialog.open}
        onClose={() => setProductDialog({ open: false, productId: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {productDialog.productId ? 'Edit Product' : 'Add Product'}
        </DialogTitle>
        <DialogContent
          sx={{ pt: 2, pb: 0, overflow: 'visible', '& .MuiInputLabel-root': { overflow: 'visible' } }}
        >
          <ProductFormContent
            compact
            productId={productDialog.productId}
            onCancel={() => setProductDialog({ open: false, productId: null })}
            onSuccess={(savedProduct) => {
              setProductDialog({ open: false, productId: null });
              if (savedProduct?.id) {
                if (productDialog.productId) {
                  setItems((prev) =>
                    prev.map((it) =>
                      it.productId === savedProduct.id
                        ? {
                            ...it,
                            name: savedProduct.name,
                            sku: savedProduct.sku,
                            brand: savedProduct.brand,
                            barcode: savedProduct.barcode || it.barcode,
                            unit: savedProduct.baseUnit || savedProduct.unit || it.unit,
                            unitPrice:
                              savedProduct.costPrice != null && savedProduct.costPrice !== ''
                                ? Number(savedProduct.costPrice)
                                : it.unitPrice,
                            sellingPrice:
                              savedProduct.sellingPrice != null && savedProduct.sellingPrice !== ''
                                ? Number(savedProduct.sellingPrice)
                                : it.sellingPrice,
                            wholesalePrice:
                              savedProduct.wholesalePrice != null && savedProduct.wholesalePrice !== ''
                                ? Number(savedProduct.wholesalePrice)
                                : it.wholesalePrice,
                          }
                        : it
                    )
                  );
                } else {
                  handleAddProduct(savedProduct);
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PurchaseOrderNoteForm;
